// src/pages/project/ProjectSettingsBoardsPage.tsx
// Boards settings: ListBoards + Create / Get / Update / Delete, with columns.

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useProjectBoard, useI18n } from '@react/hooks';
import { Button, Input, Card, CardContent, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ProjectModal, Field } from '@/features/project/ProjectModal';
import { idOf, listParams } from '@/features/project/meta';
import type { Entity } from '@core/types/entity';
import type { Board, BoardColumn } from '@core/types/project';

const BOARD_TYPE_OPTIONS = [
  { value: 'BOARD_TYPE_KANBAN', labelKey: 'project.board.typeKanban' },
  { value: 'BOARD_TYPE_SCRUM', labelKey: 'project.board.typeScrum' },
  { value: 'BOARD_TYPE_CUSTOM', labelKey: 'project.board.typeCustom' },
];

function buildColumns(names: string[]): BoardColumn[] {
  return names.map((name, i) => {
    const category =
      i === 0 ? 'COLUMN_CATEGORY_TODO' : i === names.length - 1 ? 'COLUMN_CATEGORY_DONE' : 'COLUMN_CATEGORY_IN_PROGRESS';
    return {
      id: crypto.randomUUID(),
      name,
      order: i,
      status_mappings: [],
      category: category as never,
      is_collapsed: false,
    };
  });
}

export const ProjectSettingsBoardsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const projectStore = useProject();
  const store = useProjectBoard();
  const { t } = useI18n();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Board | null>(null);
  const [detail, setDetail] = useState<Board | null>(null);
  const [deleting, setDeleting] = useState<Board | null>(null);
  const [busy, setBusy] = useState(false);

  const projectEntity = (projectStore.currentItem as never as { entity?: Entity } | null)?.entity;

  const reload = useCallback(() => id && store.ListBoards(id, listParams()), [id, store]);
  useEffect(() => {
    if (id && !projectStore.currentItem) projectStore.GetProject(id);
    reload();
  }, [id, projectStore, reload]);

  const boards: Board[] = store.items ?? [];

  const openDetail = async (board: Board) => {
    if (!id) return;
    setDetail(board);
    try {
      const full = await store.GetBoard(id, idOf(board));
      setDetail(full);
    } catch {
      /* keep list version */
    }
  };

  const del = async () => {
    if (!id || !deleting) return;
    setBusy(true);
    try {
      await store.DeleteBoard(id, idOf(deleting));
      setDeleting(null);
      reload();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t('project.section.boards')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('project.section.boardsDesc')}</p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          {t('project.board.create')}
        </Button>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {boards.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('project.board.none')}</p>
      ) : (
        <div className="space-y-3">
          {boards.map((b) => (
            <Card key={idOf(b)}>
              <CardContent className="flex items-center justify-between gap-3 p-4">
                <button
                  type="button"
                  onClick={() => openDetail(b)}
                  className="flex min-w-0 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="truncate text-sm font-medium text-foreground">{b.name}</span>
                  {b.is_default && <Badge variant="secondary" size="sm">{t('project.board.default')}</Badge>}
                </button>
                <div className="flex flex-shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditing(b);
                      setShowForm(true);
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                  {!b.is_default && (
                    <Button size="sm" variant="danger" onClick={() => setDeleting(b)}>
                      {t('common.delete')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && projectEntity && (
        <BoardFormDialog
          projectEntity={projectEntity}
          board={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            reload();
          }}
        />
      )}

      {detail && (
        <ProjectModal open title={detail.name} onClose={() => setDetail(null)}>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t('project.board.columns')}</p>
            <ul className="space-y-1">
              {(detail.columns?.columns ?? []).map((c) => (
                <li key={c.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                  <span className="text-foreground">{c.name}</span>
                  {c.wip_limit ? (
                    <span className="text-xs text-muted-foreground">
                      {t('project.board.wip')} {c.wip_limit}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </ProjectModal>
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('project.board.deleteTitle')}
        description={t('project.board.deleteDesc')}
        destructive
        isConfirming={busy}
        onConfirm={del}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
});

function BoardFormDialog({
  projectEntity,
  board,
  onClose,
  onSaved,
}: {
  projectEntity: Entity;
  board: Board | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectBoard();
  const [name, setName] = useState(board?.name ?? '');
  const [type, setType] = useState(board?.type ?? 'BOARD_TYPE_KANBAN');
  const [columns, setColumns] = useState<BoardColumn[]>(() =>
    board?.columns?.columns?.map((column) => ({ ...column })) ??
    buildColumns([t('project.board.colTodo'), t('project.board.colInProgress'), t('project.board.colDone')]),
  );
  const [busy, setBusy] = useState(false);

  const moveColumn = (index: number, offset: number) => {
    setColumns((previous) => {
      const next = [...previous];
      [next[index], next[index + offset]] = [next[index + offset], next[index]];
      return next;
    });
  };

  const originalColumnIds = new Set((board?.columns?.columns ?? []).map((column) => column.id));

  const submit = async () => {
    if (!name.trim() || columns.length === 0 || columns.some((column) => !column.name.trim())) return;
    setBusy(true);
    try {
      const nextColumns = { columns: columns.map((column, index) => ({
        ...column,
        name: column.name.trim(),
        order: index,
      })) };
      if (board) {
        await store.UpdateBoard(projectEntity.id, idOf(board), {
          project: projectEntity,
          board: { ...board, name: name.trim(), columns: nextColumns },
          field_mask: ['name', 'columns'],
        } as never);
      } else {
        await store.CreateBoard(projectEntity.id, {
          project: projectEntity,
          name: name.trim(),
          type: type as never,
          columns: nextColumns,
        } as never);
      }
      onSaved();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={board ? t('project.board.editTitle') : t('project.board.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim() || columns.length === 0 || columns.some((column) => !column.name.trim())}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.board.name')} htmlFor="board-name">
          <Input id="board-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <>
          {!board && (
            <Field label={t('project.board.type')} htmlFor="board-type">
              <Select id="board-type" value={type} onChange={(e) => setType(e.target.value as never)} aria-label={t('project.board.type')}>
                {BOARD_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label={t('project.board.columns')} hint={t('project.board.columnsHint')}>
            <div className="space-y-2">
              {columns.map((column, index) => (
                <div key={column.id} className="flex items-center gap-2">
                  <Input
                    value={column.name}
                    onChange={(event) => setColumns((previous) => previous.map((item, itemIndex) =>
                      itemIndex === index ? { ...item, name: event.target.value } : item,
                    ))}
                    aria-label={`${t('project.board.columnName')} ${index + 1}`}
                  />
                  <Button size="sm" variant="ghost" onClick={() => moveColumn(index, -1)} disabled={index === 0} aria-label={t('project.board.moveColumnUp')}>↑</Button>
                  <Button size="sm" variant="ghost" onClick={() => moveColumn(index, 1)} disabled={index === columns.length - 1} aria-label={t('project.board.moveColumnDown')}>↓</Button>
                  {!originalColumnIds.has(column.id) && (
                    <Button size="sm" variant="ghost" onClick={() => setColumns((previous) => previous.filter((item) => item.id !== column.id))} aria-label={t('project.board.removeNewColumn')}>×</Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="secondary" onClick={() => setColumns((previous) => [...previous, {
                id: crypto.randomUUID(),
                name: '',
                order: previous.length,
                status_mappings: [],
                category: 'COLUMN_CATEGORY_IN_PROGRESS' as never,
                is_collapsed: false,
              }])}>
                {t('project.board.addColumn')}
              </Button>
            </div>
          </Field>
        </>
      </div>
    </ProjectModal>
  );
}

export default ProjectSettingsBoardsPage;
